<template>
  <div
    ref="panelRef"
    class="bottom-grid-detail"
    :style="{ height: panelHeight + 'px' }"
  >
    <!-- 顶部拖拽条：按住上下拖动调整面板高度 -->
    <div
      class="bottom-grid-resizer"
      :class="{ 'is-dragging': dragging }"
      title="按住上下拖动调整高度"
      @mousedown="onDragStart"
    >
      <span class="bottom-grid-resizer-bar"></span>
    </div>

    <div
      v-if="!hideTitle"
      class="bottom-grid-header"
    >
      <span class="bottom-grid-title">{{ title }}</span>
      <a-button
        type="text"
        size="small"
        @click="emit('close')"
      >
        <CloseOutlined />
      </a-button>
    </div>

    <div class="bottom-grid-body">
      <component
        :is="detailComponent"
        :cmpObj="cmpObj"
        :extra="extra"
        :depth="depth"
      ></component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';

interface Props {
  title?: string;
  component?: any;
  cmpObj?: any;
  extra?: any;
  height?: number;
  minHeight?: number;
  depth?: number; // 嵌套层级，透传给详情组件（detailTablePro 递归用）
  hideTitle?: boolean; // 隐藏标题栏（标题与关闭按钮一起隐藏，省垂直空间）；拖拽条保留
}

const props = withDefaults(defineProps<Props>(), {
  title: '详情',
  component: null,
  cmpObj: null,
  extra: null,
  height: 300,
  minHeight: 100,
  depth: 1,
  hideTitle: false,
});

const emit = defineEmits<{
  close: [];
}>();

/** 面板上方至少给表格区保留的空间（px），拖拽上限 = 容器高度 - 该值 */
const RESERVE_HEIGHT = 100;

const panelRef = ref<HTMLElement | null>(null);
const dragging = ref(false);
const detailComponent = computed(() => props.component);
// 初始高度先取配置值（不夹取），挂载后拿到父容器实测高度再夹取
const panelHeight = ref(Number(props.height) || 300);
let start_y = 0;
let start_height = 0;

const minHeight = computed(() => Number(props.minHeight) || 0);

const getMaxHeight = () => {
  const page = panelRef.value?.parentElement;
  return Math.max((page?.clientHeight || 0) - RESERVE_HEIGHT, minHeight.value);
};

const clampHeight = (height: number) => {
  return Math.min(Math.max(height, minHeight.value), getMaxHeight());
};

const onDragMove = (e: MouseEvent) => {
  if (!dragging.value) {
    return;
  }
  // 向上拖拽 → 面板变高
  panelHeight.value = clampHeight(start_height - (e.clientY - start_y));
};

const removeDragEvents = () => {
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);
  document.body.style.userSelect = '';
  document.body.style.cursor = '';
};

const onDragEnd = () => {
  if (!dragging.value) {
    return;
  }
  dragging.value = false;
  removeDragEvents();
};

const onDragStart = (e: MouseEvent) => {
  e.preventDefault();
  dragging.value = true;
  start_y = e.clientY;
  start_height = panelHeight.value;
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd);
  document.body.style.userSelect = 'none';
  document.body.style.cursor = 'ns-resize';
};

// 窗口高度变化后可能超出可用空间，重新夹取一次
const onWindowResize = () => {
  panelHeight.value = clampHeight(panelHeight.value);
};

onMounted(() => {
  panelHeight.value = clampHeight(panelHeight.value);
  window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize);
  removeDragEvents();
});
</script>

<style lang="less" scoped>
.bottom-grid-detail {
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  background-color: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}

.bottom-grid-resizer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 8px;
  cursor: ns-resize;
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;

  &:hover,
  &.is-dragging {
    background-color: #e6f4ff;
  }

  &:hover .bottom-grid-resizer-bar,
  &.is-dragging .bottom-grid-resizer-bar {
    background-color: #1677ff;
  }
}

.bottom-grid-resizer-bar {
  width: 40px;
  height: 3px;
  border-radius: 2px;
  background-color: #d9d9d9;
  transition: background-color 0.2s ease;
}

.bottom-grid-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 4px 0 12px;
  border-bottom: 1px solid #f0f0f0;
}

.bottom-grid-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.bottom-grid-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0;
}
</style>
